import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Row,
  Col,
  Checkbox,
  Button,
  Space,
  Typography,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Tag,
  Collapse,
  theme
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faWandMagicSparkles,
  faChevronDown,
  faChevronRight,
  faPlus,
  faHeart,
  faLungs,
  faUser,
  faHandPaper,
  faStethoscope,
  faClipboardList,
  faFileAlt,
  faClipboardCheck,
  faEye,
  faCircle,
  faBrain,
  faBone,
  faFileContract
} from '@fortawesome/free-solid-svg-icons'

import { allDataPoints, getCategories } from '../data'
import InputTypeConfigurator from './InputTypeConfigurator'
import './TemplateBuilder.css'

const { Text } = Typography
const { Option } = Select
const { Panel } = Collapse

// Category icons mapping
const categoryIcons = {
  General: faFileAlt,
  Cardiac: faHeart,
  'General Appearance': faUser,
  Skin: faHandPaper,
  Respiratory: faLungs,
  Observations: faClipboardCheck,
  HEENT: faEye,
  Abdomen: faCircle,
  Neurological: faBrain,
  Orthopaedic: faBone,
  Diagnosis: faFileContract
}

const TemplateBuilder = ({ onTemplateCreate, bookingData, userRef, isModal = false }) => {
  const { token } = theme.useToken()
  const [selectedDataPoints, setSelectedDataPoints] = useState([]) // Array of selected data point objects
  const [dataPointValues, setDataPointValues] = useState({})
  const [dataPointConfigs, setDataPointConfigs] = useState({}) // Track input configurations
  const [templateName, setTemplateName] = useState('')

  const categories = useMemo(() => getCategories(), [])

  // Group all data points by category for the left panel
  const groupedAllDataPoints = useMemo(() => {
    const groups = {}
    categories.forEach((category) => {
      const categoryDataPoints = allDataPoints[category] || []
      const subGroups = {}

      categoryDataPoints.forEach((point) => {
        const subcategory = point.subcategory || 'General'
        if (!subGroups[subcategory]) {
          subGroups[subcategory] = []
        }
        subGroups[subcategory].push({
          ...point,
          category,
          categoryIcon: categoryIcons[category] || faStethoscope
        })
      })

      groups[category] = subGroups
    })
    return groups
  }, [categories])

  // Group selected data points by category for the right panel
  const selectedByCategory = useMemo(() => {
    const groups = {}
    selectedDataPoints.forEach((point) => {
      if (!groups[point.category]) {
        groups[point.category] = []
      }
      groups[point.category].push(point)
    })
    return groups
  }, [selectedDataPoints])

  const handleDataPointToggle = (dataPoint, checked) => {
    if (checked) {
      // Add data point with default input configuration
      const dataPointWithConfig = {
        ...dataPoint,
        inputConfig: {
          inputType: dataPoint.fieldType === 'multi' ? 'checkbox' : 'dropdown',
          multiSelect: dataPoint.fieldType === 'multi',
          allowOther: false,
          required: false,
          defaultValue: dataPoint.default || null,
          placeholder: '',
          helpText: ''
        }
      }

      setSelectedDataPoints((prev) => [...prev, dataPointWithConfig])

      // Set default input configuration
      setDataPointConfigs((prev) => ({
        ...prev,
        [dataPoint.id]: dataPointWithConfig.inputConfig
      }))

      // Set default value if available
      if (dataPoint.default !== undefined) {
        setDataPointValues((prev) => ({
          ...prev,
          [dataPoint.id]: dataPoint.default
        }))
      }
    } else {
      setSelectedDataPoints((prev) => prev.filter((p) => p.id !== dataPoint.id))

      // Remove configuration
      setDataPointConfigs((prev) => {
        const newConfigs = { ...prev }
        delete newConfigs[dataPoint.id]
        return newConfigs
      })

      setDataPointValues((prev) => {
        const newValues = { ...prev }
        delete newValues[dataPoint.id]
        return newValues
      })
    }
  }

  const handleInputConfigChange = (dataPointId, config) => {
    setDataPointConfigs((prev) => ({
      ...prev,
      [dataPointId]: config
    }))

    // Update the data point in selectedDataPoints with new config
    setSelectedDataPoints((prev) =>
      prev.map((point) => (point.id === dataPointId ? { ...point, inputConfig: config } : point))
    )
  }

  const handleValueChange = (dataPointId, value) => {
    setDataPointValues((prev) => ({
      ...prev,
      [dataPointId]: value
    }))
  }

  const renderDataPointInput = (dataPoint) => {
    const value = dataPointValues[dataPoint.id]

    switch (dataPoint.fieldType) {
      case 'single':
        return (
          <div className='template-builder-form-field'>
            <Select
              value={value}
              onChange={(val) => handleValueChange(dataPoint.id, val)}
              placeholder={dataPoint.placeholder}
              style={{ width: '100%' }}
              size='small'
            >
              {dataPoint.options?.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </div>
        )

      case 'multi':
        return (
          <div className='template-builder-form-field'>
            <Select
              mode='multiple'
              value={value || []}
              onChange={(val) => handleValueChange(dataPoint.id, val)}
              placeholder={dataPoint.placeholder}
              style={{ width: '100%' }}
              size='small'
            >
              {dataPoint.options?.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </div>
        )

      case 'checkbox':
        return (
          <div className='template-builder-form-field'>
            <Checkbox checked={value || false} onChange={(e) => handleValueChange(dataPoint.id, e.target.checked)}>
              {dataPoint.label}
            </Checkbox>
          </div>
        )

      case 'numeric':
        return (
          <div className='template-builder-form-field'>
            <InputNumber
              value={value}
              onChange={(val) => handleValueChange(dataPoint.id, val)}
              placeholder={dataPoint.placeholder}
              style={{ width: '100%' }}
              size='small'
              min={dataPoint.validation?.min}
              max={dataPoint.validation?.max}
              precision={dataPoint.validation?.decimals}
              addonAfter={dataPoint.units}
            />
          </div>
        )

      case 'text':
        return (
          <div className='template-builder-form-field'>
            <Input
              value={value || ''}
              onChange={(e) => handleValueChange(dataPoint.id, e.target.value)}
              placeholder={dataPoint.placeholder}
              size='small'
            />
          </div>
        )

      case 'date':
        return (
          <div className='template-builder-form-field'>
            <DatePicker
              value={value}
              onChange={(date) => handleValueChange(dataPoint.id, date)}
              style={{ width: '100%' }}
              size='small'
            />
          </div>
        )

      case 'panel':
        return (
          <div className='template-builder-form-field'>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px' }}>
              <Text strong style={{ fontSize: '12px', color: token.colorTextSecondary }}>
                {dataPoint.label} Details
              </Text>
              <Row gutter={[8, 8]} style={{ marginTop: '8px' }}>
                {dataPoint.subFields?.map((subField) => (
                  <Col key={subField.id} span={subField.ui?.span || 12}>
                    <div>
                      <Text style={{ fontSize: '11px', color: token.colorTextSecondary }}>{subField.label}</Text>
                      {renderSubFieldInput(dataPoint.id, subField)}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </div>
        )

      default:
        return (
          <div className='template-builder-form-field'>
            <Input
              value={value || ''}
              onChange={(e) => handleValueChange(dataPoint.id, e.target.value)}
              placeholder={dataPoint.placeholder}
              size='small'
            />
          </div>
        )
    }
  }

  const renderSubFieldInput = (parentId, subField) => {
    const fieldKey = `${parentId}.${subField.id}`
    const value = dataPointValues[fieldKey]

    switch (subField.fieldType) {
      case 'single':
        return (
          <Select
            value={value}
            onChange={(val) => handleValueChange(fieldKey, val)}
            style={{ width: '100%' }}
            size='small'
          >
            {subField.options?.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        )

      case 'multi':
        return (
          <Select
            mode='multiple'
            value={value || []}
            onChange={(val) => handleValueChange(fieldKey, val)}
            style={{ width: '100%' }}
            size='small'
          >
            {subField.options?.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        )

      case 'numeric':
        return (
          <InputNumber
            value={value}
            onChange={(val) => handleValueChange(fieldKey, val)}
            style={{ width: '100%' }}
            size='small'
            min={subField.validation?.min}
            max={subField.validation?.max}
            precision={subField.validation?.decimals}
            addonAfter={subField.units}
          />
        )

      case 'checkbox':
        return <Checkbox checked={value || false} onChange={(e) => handleValueChange(fieldKey, e.target.checked)} />

      default:
        return <Input value={value || ''} onChange={(e) => handleValueChange(fieldKey, e.target.value)} size='small' />
    }
  }

  const handleCreateTemplate = () => {
    if (onTemplateCreate) {
      const activeCategories = Object.keys(selectedByCategory).filter(
        (category) => selectedByCategory[category] && selectedByCategory[category].length > 0
      )

      let htmlTemplate = `<p><strong>${templateName || 'Clinical Template'}:</strong></p><br>`

      Object.entries(selectedByCategory).forEach(([category, points]) => {
        if (points.length > 0) {
          // Add category header
          htmlTemplate += `<p><strong>${category} Examination:</strong></p>`

          points.forEach((point) => {
            const value = dataPointValues[point.id]
            if (value !== undefined && value !== null && value !== '') {
              // HTML version
              htmlTemplate += `<p style="margin-left: 15px;"><strong>${point.label}:</strong> `

              let formattedValue = ''
              if (Array.isArray(value)) {
                formattedValue = value.join(', ')
              } else if (typeof value === 'boolean') {
                formattedValue = value ? 'Yes' : 'No'
              } else {
                formattedValue = value
              }

              if (point.units) {
                formattedValue += ` ${point.units}`
              }

              htmlTemplate += formattedValue + '</p>'

              // Handle subfields for panel type
              if (point.fieldType === 'panel' && point.subFields) {
                point.subFields.forEach((subField) => {
                  if (!subField) return

                  const subValue = dataPointValues[`${point.id}.${subField.id}`]
                  if (subValue !== undefined && subValue !== null && subValue !== '') {
                    // HTML version
                    htmlTemplate += `<p style="margin-left: 30px;">• <strong>${subField.label}:</strong> `

                    let formattedSubValue = ''
                    if (Array.isArray(subValue)) {
                      formattedSubValue = subValue.join(', ')
                    } else if (typeof subValue === 'boolean') {
                      formattedSubValue = subValue ? 'Yes' : 'No'
                    } else {
                      formattedSubValue = subValue
                    }

                    if (subField.units) {
                      formattedSubValue += ` ${subField.units}`
                    }

                    htmlTemplate += formattedSubValue + '</p>'
                  }
                })
              }
            }
          })

          htmlTemplate += '<br>'
        }
      })

      onTemplateCreate({
        name: templateName || 'Clinical Template',
        categories: activeCategories,
        content: htmlTemplate,
        selectedDataPoints: selectedDataPoints,
        values: dataPointValues,
        createdAt: new Date().toISOString()
      })
    }
  }

  const isDataPointSelected = (dataPoint) => {
    return selectedDataPoints.some((p) => p.id === dataPoint.id)
  }

  return (
    <div className='template-builder-container'>
      {/* Header Section */}
      <div className='template-builder-header'>
        <Input
          className='template-builder-title-input'
          placeholder='Template name (e.g., "Complete Physical Exam", "Cardiac Assessment")'
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />
      </div>

      {/* Category Overview */}
      <div className='template-builder-categories'>
        <span className='template-builder-categories-label'>Available Categories</span>
        <div className='template-builder-category-buttons'>
          {categories.map((category) => {
            const icon = categoryIcons[category] || faStethoscope
            const selectedCount = selectedDataPoints.filter((p) => p.category === category).length
            const hasData = selectedCount > 0

            return (
              <div key={category} className={`template-builder-category-btn ${hasData ? 'active' : ''}`}>
                <FontAwesomeIcon icon={icon} style={{ marginRight: '8px' }} />
                {category}
                {hasData && <span className='template-builder-category-count'>{selectedCount}</span>}
              </div>
            )
          })}
        </div>
      </div>

      {/* Main Content Panels */}
      <div className='template-builder-main'>
        {/* Left Panel - Browse Data Points */}
        <div className='template-builder-panel'>
          <div className='template-builder-panel-header'>
            <h3 className='template-builder-panel-title'>
              <FontAwesomeIcon icon={faClipboardList} className='template-builder-panel-icon' />
              Available Data Points
              <Text type='secondary' style={{ fontSize: '12px', fontWeight: 'normal' }}>
                ({selectedDataPoints.length} selected)
              </Text>
            </h3>
          </div>
          <div className='template-builder-panel-body'>
            <Collapse
              size='small'
              expandIconPosition='right'
              expandIcon={({ isActive }) => (
                <FontAwesomeIcon icon={isActive ? faChevronDown : faChevronRight} style={{ fontSize: '12px' }} />
              )}
              defaultActiveKey={['General']}
              ghost
            >
              {Object.entries(groupedAllDataPoints).map(([category, subGroups]) => {
                const icon = categoryIcons[category] || faStethoscope
                const categorySelectedCount = selectedDataPoints.filter((p) => p.category === category).length

                return (
                  <Panel
                    header={
                      <div
                        className={`template-builder-category-header ${categorySelectedCount > 0 ? 'expanded' : ''}`}
                      >
                        <Space>
                          <FontAwesomeIcon icon={icon} style={{ fontSize: '14px' }} />
                          <Text strong>{category}</Text>
                          {categorySelectedCount > 0 && (
                            <Tag size='small' color='blue' style={{ fontSize: '10px' }}>
                              {categorySelectedCount}
                            </Tag>
                          )}
                        </Space>
                      </div>
                    }
                    key={category}
                    className='template-builder-category-section'
                  >
                    {Object.entries(subGroups).map(([subcategory, points]) => (
                      <div key={`${category}-${subcategory}`} className='template-builder-subcategory'>
                        <div className='template-builder-subcategory-header'>{subcategory}</div>
                        {points.map((point) => (
                          <div
                            key={point.id}
                            className='template-builder-datapoint'
                            onClick={() => handleDataPointToggle(point, !isDataPointSelected(point))}
                          >
                            <Checkbox
                              className='template-builder-datapoint-checkbox'
                              checked={isDataPointSelected(point)}
                              onChange={(e) => e.stopPropagation()}
                            />
                            <span className='template-builder-datapoint-label'>{point.label}</span>
                            {point.units && <span className='template-builder-datapoint-units'>{point.units}</span>}
                          </div>
                        ))}
                      </div>
                    ))}
                  </Panel>
                )
              })}
            </Collapse>
          </div>
        </div>

        {/* Right Panel - Template Form */}
        <div className='template-builder-panel'>
          <div className='template-builder-panel-header'>
            <h3 className='template-builder-panel-title'>
              <FontAwesomeIcon icon={faWandMagicSparkles} className='template-builder-panel-icon' />
              Template Configuration
            </h3>
          </div>
          <div className='template-builder-panel-body'>
            {selectedDataPoints.length === 0 ? (
              <div className='template-builder-empty'>
                <FontAwesomeIcon icon={faStethoscope} className='template-builder-empty-icon' />
                <div className='template-builder-empty-text'>No data points selected</div>
                <div className='template-builder-empty-description'>
                  Select data points from the left panel to configure your template
                </div>
              </div>
            ) : (
              Object.entries(selectedByCategory).map(([category, points]) => (
                <div key={category} className='template-builder-form-section'>
                  <div className='template-builder-form-section-header'>
                    <FontAwesomeIcon icon={categoryIcons[category] || faStethoscope} />
                    {category} Examination
                    <Tag size='small' style={{ marginLeft: 'auto' }}>
                      {points.length} field{points.length > 1 ? 's' : ''}
                    </Tag>
                  </div>

                  <Row gutter={[16, 12]}>
                    {points.map((point) => (
                      <Col key={point.id} span={24}>
                        <div>
                          <label className='template-builder-form-field-label'>
                            {point.label}
                            {point.units && (
                              <Tag size='small' style={{ marginLeft: '8px', fontSize: '10px' }}>
                                {point.units}
                              </Tag>
                            )}
                          </label>
                          {renderDataPointInput(point)}

                          {/* Input Type Configurator */}
                          <InputTypeConfigurator
                            dataPoint={point}
                            initialConfig={dataPointConfigs[point.id] || {}}
                            onConfigChange={(config) => handleInputConfigChange(point.id, config)}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className='template-builder-actions'>
        <div>
          <Button
            className='template-builder-btn template-builder-btn-primary'
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={handleCreateTemplate}
            disabled={selectedDataPoints.length === 0}
          >
            Create Template
          </Button>
        </div>
      </div>
    </div>
  )
}

TemplateBuilder.propTypes = {
  onTemplateCreate: PropTypes.func,
  bookingData: PropTypes.object,
  userRef: PropTypes.object,
  isModal: PropTypes.bool
}

export default TemplateBuilder
